import React, {useState} from "react";
import {
  Users,
  TrendingUp,
  BarChart3,
  Loader,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import {usePriceRangeReport} from "../../hooks/usePriceRangeReport";
import type {PriceRangeReportQueryDto} from "../../types/priceRangeReport";

// Type definitions
type SortBy = "price" | "customerCount" | "percentage";
type SortOrder = "ASC" | "DESC";
type Period = "week" | "month" | "quarter" | "year" | "all";

interface FilterState {
  sortBy: SortBy;
  sortOrder: SortOrder;
  period: Period;
  regionId?: number | undefined;
  propertyTypeId?: number | undefined;
  listingType?: "sale" | "rent" | undefined;
}

const Dashboard: React.FC = () => {
  const [filters, setFilters] = useState<FilterState>({
    sortBy: "price",
    sortOrder: "ASC",
    period: "all",
    regionId: undefined,
    propertyTypeId: undefined,
    listingType: undefined,
  });

  // Real API call - no mock data
  const {reportData, summary, metadata, isLoading, error, refetch} =
    usePriceRangeReport(filters as PriceRangeReportQueryDto);

  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(1)} L`;
    }
    return amount.toLocaleString();
  };

  const getBarWidth = (count: number, maxCount: number): number => {
    return maxCount > 0 ? (count / maxCount) * 100 : 0;
  };

  const handleSortChange = (sortBy: SortBy, sortOrder: SortOrder): void => {
    setFilters((prev) => ({...prev, sortBy, sortOrder}));
  };

  const handlePeriodChange = (period: Period): void => {
    setFilters((prev) => ({...prev, period}));
  };

  const maxCustomerCount = Math.max(
    ...(reportData || []).map((d) => d.customerCount),
    0
  );

  // Error handling
  if (error) {
    const errorMessage =
      "data" in error
        ? (error.data as any)?.message || "Failed to load report"
        : error instanceof Error
        ? error.message
        : (error as any)?.error || "Network error occurred";

    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  Error Loading Report
                </h3>
                <p className="text-red-700 mt-1">{errorMessage}</p>
                <p className="text-sm text-red-600 mt-2">
                  Please ensure the backend API is running and accessible.
                </p>
                <button
                  onClick={refetch}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading price range report...</p>
          <p className="text-sm text-gray-500 mt-2">
            Fetching data from backend API
          </p>
        </div>
      </div>
    );
  }

  // No data state
  if (!reportData || reportData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800">
                  No Data Available
                </h3>
                <p className="text-yellow-700 mt-1">
                  No price range data found. There might not be any properties
                  or customers in the system yet.
                </p>
                <button
                  onClick={refetch}
                  className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Price Range Report
              </h1>
              <p className="text-gray-600">
                Real-time customer distribution by property price ranges
              </p>
              {metadata && (
                <p className="text-sm text-gray-500 mt-1">
                  Generated: {new Date(metadata.generatedAt).toLocaleString()} |
                  Period: {metadata.period} | Currency: {metadata.currency}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={refetch}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">
                  {summary?.totalCustomers || 0}
                </div>
                <div className="text-sm text-gray-500">Total Customers</div>
              </div>
              <BarChart3 className="w-12 h-12 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sort Controls */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Sort By
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSortChange("price", "ASC")}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    filters.sortBy === "price" && filters.sortOrder === "ASC"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Price (Low to High)
                </button>
                <button
                  onClick={() => handleSortChange("customerCount", "DESC")}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    filters.sortBy === "customerCount" &&
                    filters.sortOrder === "DESC"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Customers (High to Low)
                </button>
              </div>
            </div>

            {/* Period Filter */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Time Period
              </h3>
              <select
                value={filters.period}
                onChange={(e) => handlePeriodChange(e.target.value as Period)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="year">Last Year</option>
                <option value="quarter">Last Quarter</option>
                <option value="month">Last Month</option>
                <option value="week">Last Week</option>
              </select>
            </div>

            {/* Quick Stats */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Quick Stats
              </h3>
              <div className="text-sm text-gray-600">
                <div>
                  Total Properties:{" "}
                  <span className="font-semibold">
                    {summary?.totalProperties || 0}
                  </span>
                </div>
                <div>
                  Avg per Range:{" "}
                  <span className="font-semibold">
                    {summary?.averageCustomersPerRange?.toFixed(1) || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price Range Cards */}
        <div className="grid gap-4">
          {reportData.map((item, index) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      index === 0
                        ? "bg-green-100 text-green-600"
                        : index === 1
                        ? "bg-blue-100 text-blue-600"
                        : index === 2
                        ? "bg-purple-100 text-purple-600"
                        : index === 3
                        ? "bg-orange-100 text-orange-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item.range}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatCurrency(item.minPrice)}
                      {item.maxPrice && ` - ${formatCurrency(item.maxPrice)}`}
                      {!item.maxPrice && "+"}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-1">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-2xl font-bold text-gray-900">
                      {item.customerCount}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {item.percentage}% of customers
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-3 mb-4">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    index === 0
                      ? "bg-green-500"
                      : index === 1
                      ? "bg-blue-500"
                      : index === 2
                      ? "bg-purple-500"
                      : index === 3
                      ? "bg-orange-500"
                      : "bg-red-500"
                  }`}
                  style={{
                    width: `${getBarWidth(
                      item.customerCount,
                      maxCustomerCount
                    )}%`,
                  }}
                ></div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-100">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {item.customerCount}
                  </div>
                  <div className="text-xs text-gray-500">Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {item.propertyCount}
                  </div>
                  <div className="text-xs text-gray-500">Properties</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {item.marketShare}%
                  </div>
                  <div className="text-xs text-gray-500">Market Share</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(item.averagePrice)}
                  </div>
                  <div className="text-xs text-gray-500">Avg Price</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Statistics */}
        {summary && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {summary.totalCustomers}
                </div>
                <div className="text-sm text-gray-500">Total Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {summary.totalProperties}
                </div>
                <div className="text-sm text-gray-500">Total Properties</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {summary.mostPopularRange.customerCount}
                </div>
                <div className="text-sm text-gray-500">Most Popular Count</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {summary.averageCustomersPerRange.toFixed(1)}
                </div>
                <div className="text-sm text-gray-500">Avg per Range</div>
              </div>
            </div>

            {/* Most Popular Range */}
            {/* <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-1">
                Most Popular Price Range
              </h4>
              <p className="text-blue-700">
                <strong>{summary.mostPopularRange.range}</strong> with{" "}
                {summary.mostPopularRange.customerCount} customers
              </p>
            </div> */}
          </div>
        )}

        {/* API Status */}
        {/* <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-xs text-green-800 font-bold">✓</span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-green-800 mb-1">
                Live API Connected
              </h4>
              <p className="text-sm text-green-700">
                Data is fetched directly from the backend API endpoint{" "}
                <code className="bg-green-100 px-1 rounded">
                  {import.meta.env.VITE_API_URL}/dashboard/price-range-report
                </code>
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Dashboard;
